import { ComponentProps, FC, useCallback, useMemo } from 'react';

import classNames from 'classnames';
import { NavLink, NavLinkProps } from 'react-router-dom';

import { TestMetadata } from '../../types';
import { useResponsiveNavBar } from '../NavBarContext';

import './NavBarLink.scss';

export interface NavBarLinkProps extends ComponentProps<'li'> {
  /** Path for link to go to */
  path: string;
  /** Custom class to give to li */
  className?: string;
  /** NavLinkProps override props to spread to link */
  linkProps?: NavLinkProps;
  /** TestMetadata to use on li and link */
  testMetadata?: {
    container?: TestMetadata;
    link?: TestMetadata;
  };
  /**
   * Overrides internal is active logic. Useful when a link should be marked as active regardless of what the current path is (ex: one of its sub-links is active).
   * @param path - current route path
   * @param defaultIsActive - default is active value provided by react-router NavLink component
   */
  isActiveLink?: (path: string, defaultIsActive: boolean) => boolean;
}

const NavBarLink: FC<NavBarLinkProps> = ({
  path,
  className,
  testMetadata,
  children,
  linkProps,
  isActiveLink,
  ...otherProps
}) => {
  const { getBreakpointClassName, useMobileDrawer } = useResponsiveNavBar();

  const navBarLinkClasses = useMemo(
    () => classNames(getBreakpointClassName('sb-navbar-link'), className),
    [className, getBreakpointClassName],
  );

  const navLinkClassName = useCallback(
    (isActive: boolean) => {
      const shouldSetActiveLinkClasses =
        isActiveLink?.(path, isActive) ?? isActive;

      return classNames({
        [getBreakpointClassName('sb-navbar-link__active-link')]:
          shouldSetActiveLinkClasses,
        [getBreakpointClassName('sb-navbar-link__non-active-link')]:
          !shouldSetActiveLinkClasses,
        'sb-navbar-link_light': !useMobileDrawer,
      });
    },
    [getBreakpointClassName, isActiveLink, path, useMobileDrawer],
  );

  return (
    <li
      role="navigation"
      className={navBarLinkClasses}
      {...testMetadata?.container}
      {...otherProps}
    >
      <NavLink
        to={path}
        {...testMetadata?.link}
        className={navLinkClassName}
        {...linkProps}
      >
        {children}
      </NavLink>
    </li>
  );
};

export default NavBarLink;
